import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package } from 'lucide-react';
import { portal } from '../api';
import ConfigureModal from '../components/ConfigureModal';
import type { Product } from '../types';

export default function ProductsPage() {
  const [configuring, setConfiguring] = useState<Product | null>(null);

  const { data: products, isLoading } = useQuery({
    queryKey: ['portal', 'products'],
    queryFn: portal.products,
  });

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-52 bg-slate-100 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!products?.length) {
    return (
      <div className="bg-surface border border-slate-200 rounded-xl p-10 text-center">
        <Package className="mx-auto text-slate-300 mb-3" size={40} />
        <p className="text-slate-500">Aktuell sind keine Produkte verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Produkte</h1>
        <p className="text-sm text-slate-500">Wählen Sie ein Produkt und einen passenden Tarif.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => {
          const cheapest = product.plans
            .map((p) => Number(p.monthlyPrice ?? 0))
            .filter((n) => n > 0)
            .sort((a, b) => a - b)[0];

          return (
            <div
              key={product.id}
              className="bg-surface border border-slate-200 rounded-xl p-5 flex flex-col"
            >
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt=""
                  className="w-12 h-12 rounded-lg object-cover border border-slate-200 mb-3"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-brand-50 flex items-center justify-center mb-3">
                  <Package className="text-brand-600" size={22} />
                </div>
              )}

              <h2 className="font-semibold text-slate-900">{product.name}</h2>
              {product.description && (
                <p className="text-sm text-slate-500 mt-1 flex-1">{product.description}</p>
              )}

              {product.extraAttributes?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {product.extraAttributes.map((a) => (
                    <span
                      key={a.name}
                      className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                    >
                      {a.name}: {a.options.join(', ')}
                    </span>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  {cheapest ? (
                    <>
                      ab <span className="font-semibold text-slate-900">€{cheapest}</span>/Monat
                    </>
                  ) : (
                    `${product.plans.length} Tarif(e)`
                  )}
                </span>
                <button
                  onClick={() => setConfiguring(product)}
                  disabled={product.plans.length === 0}
                  className="bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  Konfigurieren
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {configuring && (
        <ConfigureModal product={configuring} onClose={() => setConfiguring(null)} />
      )}
    </div>
  );
}
